import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Overview } from "./Overview";
import { ByUser } from "./ByUser";
import { UserDrilldown } from "./UserDrilldown";
import { RangeBar } from "./RangeBar";
import { PickerSheet } from "./PickerSheet";
import { applyRange } from "../lib/csvAnalysis";
import { DEFAULT_SHEET, computeSheet } from "../lib/pickerSheet";
import { BreakReport } from "./BreakReport";
import { fmtNum } from "../lib/format";
import { monthLabel } from "../lib/csvAnalysis";

export const Dashboard = ({ raw }) => {
  const [tab, setTab] = useState("overview");
  const [[from, to], setRange] = useState([raw.months[0], raw.months[raw.months.length - 1]]);
  const [capIdx, setCapIdx] = useState(3);
  const [sheetCfg, setSheetCfg] = useState(DEFAULT_SHEET);
  const fullCfg = useMemo(() => ({ ...sheetCfg, holidays: raw.holidays, overrides: raw.shiftOverrides }), [sheetCfg, raw.holidays, raw.shiftOverrides]);
  const hoursByMonth = useMemo(() => {
    if (!raw.days) return null;
    const out = {};
    Object.values(raw.days).forEach((ud) => Object.entries(ud).forEach(([k, d]) => { out[k.slice(0, 7)] = (out[k.slice(0, 7)] || 0) + Math.max(0, d[3] - d[2]); }));
    return out;
  }, [raw.days]);
  const sheetUsers = useMemo(() => (tab === "breaks" && raw.days ? computeSheet(raw.days, from, to, fullCfg) : []), [tab, raw.days, from, to, fullCfg]);
  const data = useMemo(() => applyRange(raw, from, to, capIdx), [raw, from, to, capIdx]);
  const [selected, setSelected] = useState(() => data.users.slice(0, 3).map((u) => u.id));
  const [openUser, setOpenUser] = useState(null);
  const toggle = (id) => setSelected((s) => {
    if (s.includes(id)) return s.filter((x) => x !== id);
    if (s.length >= 8) { toast.warning("Max 8 users can be compared at once"); return s; }
    return [...s, id];
  });
  return (
    <div>
      <main className="max-w-[1500px] mx-auto px-4 sm:px-8 py-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-2 no-print">
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-100" data-testid="dashboard-title">{monthLabel(from)} – {monthLabel(to)}</h1>
          <p className="text-xs text-slate-500 font-mono" data-testid="dataset-meta"><span data-testid="dataset-name" className="text-slate-300">{raw.fileName}</span> · {fmtNum(data.rowsRead)} rows · {fmtNum(data.skipped)} skipped · showing {fmtNum(data.totalVisits)} visits{raw.hiddenCount ? <> · <Link to="/pickers" data-testid="hidden-pickers-badge" className="text-amber-400 hover:underline">{raw.hiddenCount} picker{raw.hiddenCount === 1 ? "" : "s"} hidden</Link></> : null}</p>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex flex-col xl:flex-row xl:items-center gap-4 mb-6 no-print">
          <TabsList className="bg-[#111827] border border-slate-800 w-fit">
            <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">Overview</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-by-user" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">By user</TabsTrigger>
            <TabsTrigger value="sheet" data-testid="tab-picker-sheet" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">Picker sheet</TabsTrigger>
            {raw.hasTime && <TabsTrigger value="breaks" data-testid="tab-break-report" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">Break report</TabsTrigger>}
          </TabsList>
          <div className="flex-1"><RangeBar months={raw.months} from={from} to={to} setRange={setRange} capIdx={capIdx} setCapIdx={setCapIdx} hasHop={raw.hasHop} /></div>
          </div>
          <TabsContent value="overview" className="fade-up"><Overview key={`${from}-${to}`} data={data} hoursByMonth={hoursByMonth} onOpenUser={setOpenUser} /></TabsContent>
          <TabsContent value="sheet" className="fade-up"><PickerSheet raw={raw} from={from} to={to} cfg={fullCfg} setCfg={setSheetCfg} /></TabsContent>
          <TabsContent value="breaks" className="fade-up"><BreakReport users={sheetUsers} cfg={fullCfg} /></TabsContent>
          <TabsContent value="users" className="fade-up"><ByUser data={data} raw={raw} selected={selected} setSelected={setSelected} toggle={toggle} onOpen={setOpenUser} /></TabsContent>
        </Tabs>
      </main>
      <UserDrilldown data={data} raw={raw} cfg={fullCfg} userId={openUser} onClose={() => setOpenUser(null)} selected={selected} onToggle={toggle} />
    </div>
  );
};
